export interface TreemapRectangle {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export function layoutTreemap(
  values: number[],
  width: number,
  height: number,
  minimumTileSize = 0,
): TreemapRectangle[] {
  const sorted = values
    .map((value, index) => ({ value, index }))
    .filter(item => Number.isFinite(item.value) && item.value > 0)
    .sort((a, b) => b.value - a.value || a.index - b.index);
  if (!sorted.length || width <= 0 || height <= 0) return [];

  // Scaling before summing avoids overflow for large financial values.
  const maximum = sorted[0].value;
  const total = sorted.reduce((sum, item) => sum + item.value / maximum, 0);
  const shares = sorted.map(item => ({
    index: item.index,
    share: item.value / maximum / total,
  }));
  const createLayout = (floor: number) => {
    const adjustedTotal = shares.reduce((sum, item) => sum + Math.max(item.share, floor), 0);
    return squarify(
      shares.map(item => ({
        index: item.index,
        area: (Math.max(item.share, floor) / adjustedTotal) * width * height,
      })),
      width,
      height,
    );
  };
  let bestLayout = createLayout(0);
  const shortestSide = (tiles: TreemapRectangle[]) =>
    Math.min(...tiles.map(tile => Math.min(tile.width, tile.height)));
  let bestSize = shortestSide(bestLayout);
  if (minimumTileSize <= 0 || bestSize >= minimumTileSize) return bestLayout;

  // Limit inflation to 12% of the original total so tiny holdings stay secondary.
  let lower = 0;
  let upper = 1 / shares.length;
  for (let iteration = 0; iteration < 24; iteration += 1) {
    const floor = (lower + upper) / 2;
    const inflation = shares.reduce((sum, item) => sum + Math.max(0, floor - item.share), 0);
    if (inflation <= 0.12) lower = floor;
    else upper = floor;
  }
  const maximumFloor = lower;
  let floor = Math.min((minimumTileSize * minimumTileSize) / (width * height), maximumFloor);
  while (floor > 0) {
    const candidate = createLayout(floor);
    const candidateSize = shortestSide(candidate);
    if (candidateSize > bestSize) {
      bestLayout = candidate;
      bestSize = candidateSize;
    }
    if (bestSize >= minimumTileSize || floor >= maximumFloor) break;
    floor = Math.min(floor * 1.5, maximumFloor);
  }
  return bestLayout;
}

function squarify(
  nodes: { index: number; area: number }[],
  width: number,
  height: number,
): TreemapRectangle[] {
  const rectangles: TreemapRectangle[] = [];
  let x = 0;
  let y = 0;
  let remainingWidth = width;
  let remainingHeight = height;
  let cursor = 0;
  const worstRatio = (sum: number, min: number, max: number, side: number) =>
    Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min));

  while (cursor < nodes.length && remainingWidth > 0 && remainingHeight > 0) {
    const start = cursor;
    const side = Math.min(remainingWidth, remainingHeight);
    let sum = nodes[cursor].area;
    const max = sum;
    let min = sum;
    cursor += 1;
    while (cursor < nodes.length) {
      const next = nodes[cursor].area;
      if (worstRatio(sum + next, next, max, side) > worstRatio(sum, min, max, side)) break;
      sum += next;
      min = next;
      cursor += 1;
    }

    // Descending strips consume the left or top edge, creating nested L shapes.
    const vertical = remainingWidth >= remainingHeight;
    const thickness =
      cursor === nodes.length ? (vertical ? remainingWidth : remainingHeight) : sum / side;
    let offset = 0;
    for (let index = start; index < cursor; index += 1) {
      const length = index === cursor - 1 ? side - offset : nodes[index].area / thickness;
      rectangles.push({
        index: nodes[index].index,
        x: vertical ? x : x + offset,
        y: vertical ? y + offset : y,
        width: vertical ? thickness : length,
        height: vertical ? length : thickness,
      });
      offset += length;
    }
    if (vertical) {
      x += thickness;
      remainingWidth -= thickness;
    } else {
      y += thickness;
      remainingHeight -= thickness;
    }
  }
  return rectangles;
}
